import React, { useMemo } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { tokens } from '../../theme';
import { useChartTheme } from '../../themes/chartTheme';
import { determineAggregation, formatGerman } from '../../helpers/chartUtils';

export interface CoregTimelinePoint {
  date: string;
  total: number;
  both_new: number;
  mc_new_vnr_dup: number;
  mc_dup_vnr_new: number;
  both_dup: number;
  incomplete: number;
}

interface CoregTimelineChartProps {
  data: CoregTimelinePoint[];
  startDate: Date;
  endDate: Date;
  viewMode: 'absolute' | 'relative';
  onViewModeChange: (mode: 'absolute' | 'relative') => void;
}

const getISOWeek = (date: Date): number => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

// ISO week-year. Thursday of the same ISO week determines the year,
// so dates like 2025-12-29 (ISO week 1 of 2026) return 2026, not 2025.
const getISOYear = (date: Date): number => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  return d.getFullYear();
};

const bucketKeys = [
  'both_new',
  'mc_dup_vnr_new',
  'mc_new_vnr_dup',
  'both_dup',
  'incomplete',
] as const;

const bucketLabels: Record<(typeof bucketKeys)[number], string> = {
  both_new: 'Beide neu',
  mc_dup_vnr_new: 'Nur MC-Dublette',
  mc_new_vnr_dup: 'Nur VNR-Dublette',
  both_dup: 'Beide Dublette (verschwendet)',
  incomplete: 'Unvollständig / Fehler',
};

function formatBucketKey(date: Date, aggregation: 'daily' | 'weekly' | 'monthly'): string {
  switch (aggregation) {
    case 'monthly':
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    case 'weekly': {
      const week = String(getISOWeek(date)).padStart(2, '0');
      return `${getISOYear(date)}-W${week}`;
    }
    default:
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
        date.getDate(),
      ).padStart(2, '0')}`;
  }
}

function formatBucketDisplay(key: string, aggregation: 'daily' | 'weekly' | 'monthly'): string {
  if (aggregation === 'monthly') {
    const [y, m] = key.split('-');
    return `${m}/${y}`;
  }
  if (aggregation === 'weekly') {
    const [year, week] = key.split('-W');
    return `KW ${week}/${year.slice(-2)}`;
  }
  const [, m, d] = key.split('-');
  return `${d}.${m}.`;
}

const CoregTimelineChart: React.FC<CoregTimelineChartProps> = ({
  data,
  startDate,
  endDate,
  viewMode,
  onViewModeChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const chartTheme = useChartTheme();

  const aggregation = useMemo(() => determineAggregation(startDate, endDate), [startDate, endDate]);

  const aggregated = useMemo(() => {
    const map = new Map<string, CoregTimelinePoint>();
    data.forEach((point) => {
      const d = new Date(point.date);
      const key = formatBucketKey(d, aggregation);
      const existing = map.get(key);
      if (existing) {
        existing.total += point.total;
        existing.both_new += point.both_new;
        existing.mc_new_vnr_dup += point.mc_new_vnr_dup;
        existing.mc_dup_vnr_new += point.mc_dup_vnr_new;
        existing.both_dup += point.both_dup;
        existing.incomplete += point.incomplete;
      } else {
        map.set(key, { ...point, date: key });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [data, aggregation]);

  const chartData = useMemo(() => {
    return aggregated.map((point) => {
      const display = formatBucketDisplay(point.date, aggregation);
      if (viewMode === 'absolute') {
        return {
          date: display,
          both_new: point.both_new,
          mc_dup_vnr_new: point.mc_dup_vnr_new,
          mc_new_vnr_dup: point.mc_new_vnr_dup,
          both_dup: point.both_dup,
          incomplete: point.incomplete,
        };
      }
      const denom = point.total > 0 ? point.total : 1;
      return {
        date: display,
        both_new: (100 * point.both_new) / denom,
        mc_dup_vnr_new: (100 * point.mc_dup_vnr_new) / denom,
        mc_new_vnr_dup: (100 * point.mc_new_vnr_dup) / denom,
        both_dup: (100 * point.both_dup) / denom,
        incomplete: (100 * point.incomplete) / denom,
      };
    });
  }, [aggregated, viewMode, aggregation]);

  const bucketColors = [
    colors.greenAccent[500],
    '#f9a825',
    '#ef6c00',
    colors.redAccent[500],
    colors.grey[500],
  ];

  if (data.length === 0) {
    return (
      <Box p={3} textAlign="center" color="text.secondary">
        Keine Daten für den ausgewählten Zeitraum.
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          size="small"
          onChange={(_e, value) => value && onViewModeChange(value)}
          sx={{
            '& .MuiToggleButton-root': {
              color: colors.grey[100],
              borderColor: colors.primary[300],
              '&.Mui-selected': {
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
              },
            },
          }}
        >
          <ToggleButton value="absolute">Absolut</ToggleButton>
          <ToggleButton value="relative">Relativ (%)</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box height="440px">
        <ResponsiveBar
          data={chartData as any}
          keys={[...bucketKeys]}
          indexBy="date"
          theme={chartTheme}
          margin={{ top: 20, right: 220, bottom: 80, left: 80 }}
          padding={0.3}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={bucketColors}
          borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          axisBottom={{
            tickSize: 5,
            tickPadding: 10,
            tickRotation: -30,
            legend: aggregation === 'monthly' ? 'Monat' : aggregation === 'weekly' ? 'KW' : 'Datum',
            legendPosition: 'middle',
            legendOffset: 64,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 10,
            tickRotation: 0,
            legend: viewMode === 'absolute' ? 'Anzahl Leads' : 'Anteil (%)',
            legendPosition: 'middle',
            legendOffset: -60,
            format: (value) =>
              viewMode === 'relative'
                ? `${Number(value).toFixed(0)}%`
                : formatGerman.format(Number(value)),
          }}
          valueFormat={(value) =>
            viewMode === 'relative'
              ? `${Number(value).toFixed(1)}%`
              : formatGerman.format(Number(value))
          }
          labelSkipWidth={14}
          labelSkipHeight={14}
          labelTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
          legends={[
            {
              dataFrom: 'keys',
              anchor: 'bottom-right',
              direction: 'column',
              justify: false,
              translateX: 200,
              translateY: 0,
              itemsSpacing: 4,
              itemWidth: 180,
              itemHeight: 20,
              itemDirection: 'left-to-right',
              itemOpacity: 0.85,
              symbolSize: 12,
              symbolShape: 'square',
              data: bucketKeys.map((key, idx) => ({
                id: key,
                label: bucketLabels[key],
                color: bucketColors[idx],
              })),
            },
          ]}
          tooltip={({ id, value, indexValue }) => (
            <Box
              sx={{
                padding: '8px 12px',
                color: '#222',
                background: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              <strong>{indexValue}</strong>
              <br />
              {bucketLabels[id as (typeof bucketKeys)[number]]}:{' '}
              {viewMode === 'absolute'
                ? formatGerman.format(Number(value))
                : `${Number(value).toFixed(1)}%`}
            </Box>
          )}
          role="application"
          ariaLabel="Co-Reg Efficiency Timeline"
        />
      </Box>
    </Box>
  );
};

export default CoregTimelineChart;
